import React, { useState, useRef, useEffect } from 'react';
import './Bookmarks.css';

/**
 * CreateBookmarkFolderButton - allows users to create new bookmark folders.
 * The list adds the folder immediately; the parent saves it.
 */
function CreateBookmarkFolderButton({ containerId, onCreate }) {
  const [isCreating, setIsCreating] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isCreating && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCreating]);

  const handleStartCreate = () => {
    setIsCreating(true);
    setFolderName('');
    setError(null);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setFolderName('');
    setError(null);
  };

  const handleCreate = () => {
    const trimmedName = folderName.trim();

    if (!trimmedName) {
      setError('Folder name cannot be empty');
      return;
    }

    if (trimmedName.length > 255) {
      setError('Folder name is too long (max 255 characters)');
      return;
    }

    setIsCreating(false);
    setFolderName('');
    setError(null);
    onCreate?.(trimmedName);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreate();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (!isCreating) {
    return (
      <button
        className="create-bookmark-folder-button"
        onClick={handleStartCreate}
        disabled={!containerId}
        title="Create a new folder"
      >
        <span className="create-bookmark-folder-button__icon">📁+</span>
        <span className="create-bookmark-folder-button__text">New Folder</span>
      </button>
    );
  }

  return (
    <div className="create-bookmark-folder-form">
      <input
        ref={inputRef}
        type="text"
        className="create-bookmark-folder-input"
        placeholder="Folder name..."
        value={folderName}
        onChange={(e) => setFolderName(e.target.value)}
        onKeyDown={handleKeyDown}
        maxLength={255}
      />

      <div className="create-bookmark-folder-actions">
        <button
          className="create-bookmark-folder-confirm"
          onClick={handleCreate}
          disabled={!folderName.trim()}
          title="Create folder"
        >
          ✓
        </button>
        <button
          className="create-bookmark-folder-cancel"
          onClick={handleCancel}
          title="Cancel"
        >
          ✕
        </button>
      </div>

      {error && (
        <div className="create-bookmark-folder-error">{error}</div>
      )}
    </div>
  );
}

export default CreateBookmarkFolderButton;
